/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * MessageComposer - Text input for composing and sending messages
 */

import { useState, useRef, useEffect } from 'react'
import { Send, X, Loader2 } from 'lucide-react'

const MessageComposer = ({
  onSend,
  replyTo = null,
  onCancelReply,
  disabled = false,
  placeholder = 'Type a message...'
}) => {
  const [content, setContent] = useState('')
  const [sending, setSending] = useState(false)
  const textareaRef = useRef(null)

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`
    }
  }, [content])

  // Focus on textarea when replying
  useEffect(() => {
    if (replyTo && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [replyTo])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!content.trim() || sending || disabled) return

    try {
      setSending(true)
      await onSend(content.trim(), replyTo?.id)
      setContent('')
      onCancelReply?.()
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      {/* Reply indicator */}
      {replyTo && (
        <div className="flex items-center justify-between mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
          <div className="flex-1 min-w-0">
            <span className="text-gray-500 dark:text-gray-400">Replying to: </span>
            <span className="text-gray-700 dark:text-gray-300 truncate">
              {replyTo.content.slice(0, 60)}
              {replyTo.content.length > 60 && '...'}
            </span>
          </div>
          <button
            onClick={onCancelReply}
            className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || sending}
          rows={1}
          className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600
            bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100
            placeholder:text-gray-500 dark:placeholder:text-gray-400
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <button
          type="submit"
          disabled={!content.trim() || sending || disabled}
          className="btn btn-primary p-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  )
}

export default MessageComposer
